import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Plus,
  Search,
  Filter,
  FolderOpen,
  Users,
  Calendar,
  DollarSign,
  Clock,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
  RefreshCw,
  Loader2
} from 'lucide-react';

interface Project {
  _id: string;
  title: string;
  description: string;
  status: string;
  progress: number;
  deadline: string;
  budget: {
    amount: number;
    currency: string;
  };
  team: any[];
  client: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  category: string;
  priority: string;
  createdAt: string;
  statusLabel?: string;
  statusColor?: string;
}

interface ApiResponse {
  projects: Project[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  meta: {
    total: number;
    role: string;
    message?: string;
  };
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

// Loading skeleton component
const ProjectSkeleton: React.FC<{ theme: string }> = ({ theme }) => (
  <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6 rounded-lg shadow-sm border animate-pulse`}>
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <div className={`h-5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-3/4 mb-2`}></div>
        <div className={`h-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-full mb-1`}></div>
        <div className={`h-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-2/3`}></div>
      </div>
      <div className={`h-6 w-16 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-full`}></div>
    </div>
    
    <div className="space-y-3">
      <div className="flex justify-between">
        <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-16`}></div>
        <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-12`}></div>
      </div>
      <div className={`h-2 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-full`}></div>
      <div className="flex justify-between">
        <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-20`}></div>
        <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-24`}></div>
      </div>
    </div>
  </div>
);

const ProjectsList: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const API_BASE_URL = getApiBaseUrl();

  // Debounced search function
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const debouncedSearch = useCallback((term: string) => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    const timer = setTimeout(() => {
      fetchProjects(1, term, statusFilter, categoryFilter);
    }, 500);
    
    setSearchDebounceTimer(timer);
  }, [statusFilter, categoryFilter]);

  const fetchProjects = useCallback(async (
    page: number = 1, 
    search: string = '', 
    status: string = 'all', 
    category: string = 'all',
    showRefreshing: boolean = false
  ) => {
    try {
      if (page === 1) {
        setLoading(true);
      }
      if (showRefreshing) {
        setRefreshing(true);
      }
      setError('');
      
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('No authentication token found');

      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });
      
      if (search.trim()) params.append('search', search.trim());
      if (status !== 'all') params.append('status', status);
      if (category !== 'all') params.append('category', category);

      const response = await fetch(`${API_BASE_URL}/projects?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data: ApiResponse = await response.json();
        setProjects(data.projects || []);
        setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
        
        if (user?.role === 'developer' && data.projects?.length === 0) {
          setError(data.meta?.message || 'You have no projects assigned yet');
        }
      } else if (response.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to fetch projects`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load projects. Please try again.');
      setProjects([]);
      setPagination({ page: 1, limit: 20, total: 0, pages: 0 });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [API_BASE_URL, navigate, user?.role, pagination.limit]);

  // Initial load
  useEffect(() => {
    if (user) {
      fetchProjects(1, searchTerm, statusFilter, categoryFilter);
    }
  }, [user, statusFilter, categoryFilter]);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  // Handle filter changes
  const handleStatusFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setStatusFilter(value);
    fetchProjects(1, searchTerm, value, categoryFilter);
  }, [fetchProjects, searchTerm, categoryFilter]);

  const handleCategoryFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setCategoryFilter(value);
    fetchProjects(1, searchTerm, statusFilter, value);
  }, [fetchProjects, searchTerm, statusFilter]);

  // Manual refresh
  const handleRefresh = useCallback(() => {
    fetchProjects(pagination.page, searchTerm, statusFilter, categoryFilter, true);
  }, [fetchProjects, pagination.page, searchTerm, statusFilter, categoryFilter]);

  // Memoized helper functions
  const getStatusColor = useMemo(() => (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      case 'on-hold': return 'text-gray-600 bg-gray-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  }, []);

  const getPriorityColor = useMemo(() => (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  }, []);

  const formatCurrency = useMemo(() => (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  }, []);

  const getTimeUntilDeadline = useMemo(() => (deadline: string) => {
    const days = Math.ceil((new Date(deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    return `${days} days left`;
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Projects</h1>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Loading your projects...</p>
          </div>
          <div className="flex items-center space-x-2">
            <Loader2 className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} animate-spin`} />
          </div>
        </div>

        <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6 rounded-lg shadow-sm border`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex-1 max-w-md">
              <div className={`h-10 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-lg animate-pulse`}></div>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`h-10 w-32 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-lg animate-pulse`}></div>
              <div className={`h-10 w-40 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-lg animate-pulse`}></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <ProjectSkeleton key={index} theme={theme} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Projects</h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
            {user?.role === 'client' 
              ? 'Manage and track your projects' 
              : user?.role === 'developer'
                ? 'View and work on your assigned projects'
                : 'Manage and track all projects'
            }
            {pagination.total > 0 && (
              <span className="ml-2">({pagination.total} total)</span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`p-2 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors duration-200 disabled:opacity-50`}
            title="Refresh projects"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          {user?.role === 'client' && (
            <Link
              to="/dashboard/projects/create"
              className={`flex items-center px-4 py-2 ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors duration-200`}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Link>
          )}
        </div>
      </div>

      {error && (
        <div className={`${
          theme === 'dark' ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
        } border rounded-lg p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className={`w-5 h-5 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'} mr-2`} />
              <span className={`${theme === 'dark' ? 'text-red-300' : 'text-red-700'} text-sm`}>{error}</span>
            </div>
            <button
              onClick={handleRefresh}
              className={`text-sm ${theme === 'dark' ? 'text-red-300 hover:text-red-200' : 'text-red-600 hover:text-red-500'} underline`}
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6 rounded-lg shadow-sm border`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} w-4 h-4`} />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={handleSearchChange}
                className={`pl-10 pr-4 py-2 w-full border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200`}
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className={`px-3 py-2 border ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200`}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={categoryFilter}
              onChange={handleCategoryFilterChange}
              className={`px-3 py-2 border ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200`}
            >
              <option value="all">All Categories</option>
              <option value="web-development">Web Development</option>
              <option value="mobile-development">Mobile Development</option>
              <option value="desktop-development">Desktop Development</option>
              <option value="data-science">Data Science</option>
              <option value="ai-ml">AI/ML</option>
              <option value="blockchain">Blockchain</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className={`text-center py-12 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-sm border`}>
          <FolderOpen className={`w-16 h-16 ${theme === 'dark' ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
            {user?.role === 'developer' 
              ? "No projects assigned" 
              : "No projects found"}
          </h3>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
            {error
              ? "There was an error loading projects. Please try refreshing."
              : searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                ? "Try adjusting your search or filter criteria"
                : user?.role === 'client'
                  ? "You haven't created any projects yet"
                  : user?.role === 'developer'
                    ? "You haven't been assigned to any projects yet"
                    : "No projects exist in the system"
            }
          </p>
          {user?.role === 'client' && !searchTerm && statusFilter === 'all' && categoryFilter === 'all' && (
            <Link
              to="/dashboard/projects/create"
              className={`inline-flex items-center px-4 py-2 ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors duration-200`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project._id}
                className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'} rounded-lg shadow-sm border hover:shadow-md transition-all duration-200`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/dashboard/projects/${project._id}`}
                        className={`text-lg font-semibold ${theme === 'dark' ? 'text-white hover:text-blue-400' : 'text-gray-900 hover:text-blue-600'} truncate block transition-colors duration-200`}
                      >
                        {project.title}
                      </Link>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1 line-clamp-2`}>
                        {project.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(project.status)}`}
                      >
                        {project.status.replace('-', ' ')}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Progress</span>
                      <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>{project.progress}%</span>
                    </div>
                    <div className={`w-full ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>

                    <div className={`flex items-center justify-between text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {project.team.length} member{project.team.length !== 1 ? 's' : ''}
                      </div>
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        {formatCurrency(project.budget.amount, project.budget.currency)}
                      </div>
                    </div>

                    <div className={`flex items-center justify-between text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {getTimeUntilDeadline(project.deadline)}
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getPriorityColor(project.priority)}`}
                      >
                        {project.priority}
                      </span>
                    </div>
                  </div>

                  <div className={`mt-4 pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Client: {project.client.firstName} {project.client.lastName}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/dashboard/projects/${project._id}`}
                          className={`p-2 ${theme === 'dark' ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700' : 'text-gray-400 hover:text-blue-600 hover:bg-gray-100'} rounded-lg transition-colors duration-200`}
                          title="View project"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-center space-x-2 mt-8">
              <button
                onClick={() => fetchProjects(pagination.page - 1, searchTerm, statusFilter, categoryFilter)}
                disabled={pagination.page <= 1}
                className={`px-3 py-2 text-sm ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400'
                } rounded-lg transition-colors duration-200 disabled:cursor-not-allowed`}
              >
                Previous
              </button>
              
              <span className={`px-3 py-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Page {pagination.page} of {pagination.pages}
              </span>
              
              <button
                onClick={() => fetchProjects(pagination.page + 1, searchTerm, statusFilter, categoryFilter)}
                disabled={pagination.page >= pagination.pages}
                className={`px-3 py-2 text-sm ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400'
                } rounded-lg transition-colors duration-200 disabled:cursor-not-allowed`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProjectsList;

